import React, { useMemo, useState } from 'react';
import { Typography, Box, Chip, Button } from '@mui/material';
import { LabourCostRateUserOverride } from 'api/labourApi';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteLabourCostRateUserOverride } from 'api/labourApi';
import ConfirmDeleteDialog from 'components/ConfirmDeleteDialog/ConfirmDeleteDialog';
import { Alert } from '@mui/material';
import { format } from 'date-fns';
import { currencyType } from 'api/organizationApi';
import MUIDataTable, { MUIDataTableColumnDef } from 'mui-datatables';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

interface LabourCostRateUserOverrideTableProps {
  costRateUserOverrides: LabourCostRateUserOverride[];
  onRowClick?: (id: string) => void;
  onAddNewClick: () => void;
  onImportClick: () => void;
  isFetching?: boolean;
}

type TableRow = {
  id: string;
  userName: string;
  userRole: string;
  rate: number;
  workingAwayFromHomeRate: number;
  effectiveFrom: string;
  _raw: LabourCostRateUserOverride;
};

export default function LabourCostRateUserOverrideTable({
  costRateUserOverrides,
  onRowClick,
  onAddNewClick,
  onImportClick,
  isFetching = false,
}: LabourCostRateUserOverrideTableProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] =
    useState<LabourCostRateUserOverride | null>(null);
  const queryClient = useQueryClient();

  const deleteMutation = useMutation(deleteLabourCostRateUserOverride, {
    onSuccess: () => {
      queryClient.invalidateQueries(['labour-rates']);
    },
    onError: (error) => {
      console.error('Error deleting cost rate user override:', error);
    },
  });

  const handleDelete = async () => {
    if (!selectedItem) return;
    deleteMutation.mutate(selectedItem.id);
    setDeleteDialogOpen(false);
    setSelectedItem(null);
  };

  // Group rates by user and get latest for each
  const latestRates = useMemo(() => {
    const groupedRates = costRateUserOverrides.reduce((acc, rate) => {
      const userId = rate.userId;
      if (!acc[userId]) acc[userId] = [];
      acc[userId].push(rate);
      return acc;
    }, {} as Record<string, LabourCostRateUserOverride[]>);
    return Object.values(groupedRates).map((rates) =>
      rates.reduce(
        (latest, current) =>
          new Date(current.effectiveFrom) > new Date(latest.effectiveFrom)
            ? current
            : latest,
        rates[0]
      )
    );
  }, [costRateUserOverrides]);

  // Flat data for MUIDataTable search/filter
  const data: TableRow[] = useMemo(
    () =>
      latestRates.map((rate) => {
        const displayRole =
          rate.user?.role === 'ProjectManager'
            ? 'Project Coordinator'
            : rate.user?.role || 'No role';
        return {
          id: rate.id,
          userName: rate.user
            ? `${rate.user.firstName} ${rate.user.lastName}`
            : 'Unknown User',
          userRole: displayRole,
          rate: Number(rate.rate),
          workingAwayFromHomeRate: Number(rate.workingAwayFromHomeRate),
          effectiveFrom: rate.effectiveFrom,
          _raw: rate,
        };
      }),
    [latestRates]
  );

  const roleFilterOptions = useMemo(() => {
    const roles = Array.from(
      new Set(data.map((row) => row.userRole).filter(Boolean))
    ).sort();
    return roles;
  }, [data]);

  const columns: MUIDataTableColumnDef[] = useMemo(
    () => [
      {
        name: 'userName',
        label: 'User',
        options: {
          filter: false,
          sort: true,
          customBodyRender: (
            _: unknown,
            { rowIndex }: { rowIndex: number }
          ) => {
            const row = data[rowIndex];
            const rate = row._raw;
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 0.5,
                }}>
                <Typography variant="body1">{row.userName}</Typography>
                {rate.user?.isLimitedUser && (
                  <Chip
                    label="Limited User"
                    size="small"
                    variant="outlined"
                    sx={{ ml: 0.5 }}
                  />
                )}
              </Box>
            );
          },
        },
      },
      {
        name: 'userRole',
        label: 'Role',
        options: {
          filter: true,
          sort: true,
          filterType: 'checkbox',
          filterOptions: {
            names: roleFilterOptions,
            logic: (role: string, filters: string[]) => {
              if (filters.length === 0) return true;
              return !filters.includes(role);
            },
          },
          customBodyRender: (
            _: unknown,
            { rowIndex }: { rowIndex: number }
          ) => {
            return (
              <Chip
                label={data[rowIndex].userRole}
                size="small"
                variant="outlined"
              />
            );
          },
        },
      },
      {
        name: 'rate',
        label: 'Rate',
        options: {
          filter: false,
          sort: true,
          customBodyRender: (value: number) =>
            `${currencyType}${Number(value).toFixed(2)}`,
        },
      },
      {
        name: 'workingAwayFromHomeRate',
        label: 'Working Away From Home Rate',
        options: {
          filter: false,
          sort: true,
          customBodyRender: (value: number) =>
            `${currencyType}${Number(value).toFixed(2)}`,
        },
      },
      {
        name: 'effectiveFrom',
        label: 'Effective From',
        options: {
          filter: false,
          sort: true,
          customBodyRender: (value: string) =>
            format(new Date(value), 'dd MMM yyyy'),
        },
      },
    ],
    [data, roleFilterOptions]
  );

  return (
    <Box>
      <MUIDataTable
        data={data}
        columns={columns}
        options={{
          elevation: 0,
          responsive: 'vertical',
          filter: true,
          sort: false,
          search: true,
          print: false,
          download: false,
          selectableRows: 'none',
          viewColumns: false,
          textLabels: {
            body: {
              noMatch: isFetching ? 'Loading...' : 'No matching records found',
            },
          },
          customToolbar: () => (
            <>
              <Button
                variant="contained"
                size="large"
                disableElevation
                onClick={onImportClick}
                sx={{ px: '2rem', ml: 2, mr: 1.5 }}
                startIcon={<CloudUploadIcon />}>
                Import
              </Button>
              <Button
                id="demo-customized-button"
                aria-haspopup="true"
                variant="contained"
                size="large"
                disableElevation
                onClick={onAddNewClick}
                sx={{
                  px: '2rem',
                }}
                startIcon={<AddIcon />}>
                Add Cost Rate by Employee
              </Button>
            </>
          ),
          onRowClick: (_rowData, rowMeta) => {
            const row = data[rowMeta.rowIndex];
            if (row?.id) onRowClick?.(row.id);
          },
        }}
        title=""
      />

      {deleteDialogOpen && selectedItem && (
        <ConfirmDeleteDialog
          open={deleteDialogOpen}
          title="Delete Confirmation"
          content={
            <>
              Are you sure you want to delete the cost rate override for{' '}
              <b>
                {selectedItem.user
                  ? `${selectedItem.user.firstName} ${selectedItem.user.lastName}`
                  : 'this user'}
              </b>{' '}
              with effective date{' '}
              <b>{new Date(selectedItem.effectiveFrom).toDateString()}</b>?
              <Alert severity="warning" style={{ marginTop: '1rem' }}>
                This action is irreversible and will permanently delete the
                data.
              </Alert>
            </>
          }
          deleteFunc={handleDelete}
          handleReject={() => {
            setDeleteDialogOpen(false);
            setSelectedItem(null);
          }}
          handleClose={() => {
            setDeleteDialogOpen(false);
            setSelectedItem(null);
          }}
        />
      )}
    </Box>
  );
}
